import { AvatarV2, Dropdown, Text, TooltipV2 } from '@cimplico/components';
import { ChatBubbleLeftIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import TaskCheckbox from '../../../components/task-checkbox/TaskCheckbox';
import { toastNotification } from '../../../components/toast/Toast';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { useUpdateChecklistItemStatus } from '../../../queries/use-update-checklist-item-status';
import { WorksheetChecklistItemStatusEnum as StatusEnum, WorksheetChecklistItem } from '../../../types/worksheet-checklist-item';
import { calcRelativeTime } from '../../../utilities/formatting';
import ChecklistItemActiveConfirm from './ChecklistItemActiveConfirm';
import ChecklistItemNotApplicableConfirm from './ChecklistItemNotApplicableConfirm';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../../intercom-events';

type ChecklistItemProps = {
    worksheetId: string;
    item: WorksheetChecklistItem;
    compactView?: boolean;
};

function ChecklistItem({ worksheetId, item, compactView = false }: ChecklistItemProps): JSX.Element {
    const [action, setAction] = useState<string | undefined>(undefined);
    const updateChecklistItemQuery = useUpdateChecklistItemStatus(worksheetId);
    const { readOnly } = useWorkpaperContext();
    const { trackEvent } = useIntercom();
    function update(status: StatusEnum, reason?: string): void {
        updateChecklistItemQuery.mutate(
            {
                checklistItemId: item.id,
                notApplicableReason: reason,
                status,
            },
            {
                onSuccess: () => {
                    toastNotification('success', 'Checklist item updated!');
                    if (status === StatusEnum.CHECKED) {
                        trackEvent(intercomEvents.checklist.completed.event);
                    } else if (status === StatusEnum.NOT_APPLICABLE) {
                        trackEvent(intercomEvents.checklist.notApplicable.event);
                    }
                    setAction(undefined);
                },
            }
        );
    }

    function handleStatusChange(currentStatus: StatusEnum): void {
        switch (currentStatus) {
            case StatusEnum.UNCHECKED:
                update(StatusEnum.CHECKED);
                break;
            case StatusEnum.CHECKED:
                update(StatusEnum.UNCHECKED);
                break;
            case StatusEnum.NOT_APPLICABLE:
                setAction('ACTIVATE');
                break;
            default:
                break;
        }
    }

    const stripColour = item.status === StatusEnum.NOT_APPLICABLE ? 'bg-red-500' : 'bg-green-500';

    return (
        <>
            <ChecklistItemNotApplicableConfirm
                isOpen={action === 'NOT_APPLICABLE'}
                onClose={(): void => setAction(undefined)}
                handleNotApplicable={(reason): void => {
                    update(StatusEnum.NOT_APPLICABLE, reason);
                }}
            />
            <ChecklistItemActiveConfirm
                isOpen={action === 'ACTIVATE'}
                onClose={(): void => setAction(undefined)}
                handleActive={(): void => update(StatusEnum.UNCHECKED)}
            />

            <div className="p-2 border border-gray-300 rounded-md w-full relative overflow-hidden">
                {item.status !== StatusEnum.UNCHECKED && <div className={`absolute w-1 h-full left-0 top-0 ${stripColour}`}></div>}
                <div className="min-h-[64px] w-full flex flex-col justify-between">
                    <div className="flex mt-2">
                        <TaskCheckbox
                            status={item.status}
                            onChange={(v): void => {
                                handleStatusChange(StatusEnum[v]);
                            }}
                            disabled={readOnly}
                        />
                        <label className="text-gray-500 text-sm ml-2">{item.reference}</label>
                        <div className="ml-1">
                            <Text className="text-sm text-gray-800 mr-8" lines={2}>
                                {item.name}
                            </Text>
                            {item.description && (
                                <p className="text-xs text-gray-500 mt-1 mr-4 mb-2 whitespace-pre-line">{item.description}</p>
                            )}
                        </div>
                        <div className="right-0 top-2 absolute ml-2">
                            <Dropdown disabled={readOnly}>
                                <Dropdown.Option onClick={(): void => setAction('NOT_APPLICABLE')} label="Not applicable" />
                            </Dropdown>
                        </div>
                    </div>
                    <div className="flex items-center ml-7 mt-1">
                        {item.notApplicableReason && (
                            <TooltipV2 message={item.notApplicableReason} disabled={!compactView}>
                                <div className="flex items-center">
                                    <ChatBubbleLeftIcon className="text-gray-500 h-4 w-4" />
                                    <label className="text-sm text-gray-500 ml-1">{compactView ? '1' : item.notApplicableReason}</label>
                                </div>
                            </TooltipV2>
                        )}
                        {item.userAction?.id && (
                            <div className="ml-auto w-fit flex items-center mt-1">
                                <label className="mr-2 text-xs text-gray-500">{calcRelativeTime(item.userAction.date)}</label>
                                <AvatarV2
                                    size="mini"
                                    src={item.userAction.avatarUrl}
                                    alt={`${item.userAction.firstName[0]}${item.userAction.lastName[0]}`}
                                    tooltip={`${item.userAction.firstName} ${item.userAction.lastName}`}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
export default ChecklistItem;
